// Ten frames (22 Oct 2026, Year 2): one or two frames of 2 rows of 5 boxes,
// filled in order along the top row and then the bottom row. A `ten_frame`
// lists how many boxes are filled in each frame. For adding on, `added`
// says how many of those (the last ones filled) are a second kind of
// counter, drawn as a ring with a dot instead of a solid disc. How many
// there are is never written or spoken: counting them is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type TenFrameSpec = {
  type: 'ten_frame';
  // How many boxes are filled in each frame, 0 to 10.
  frames: number[];
  // How many of the filled boxes in each frame hold the second kind of
  // counter; all 0 when there is only one kind.
  added: number[];
};

const MAX_FRAMES = 2;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

export function readTenFrame(
  record: Record<string, unknown>,
): TenFrameSpec | null {
  const { frames } = record;
  if (!Array.isArray(frames)) return null;
  if (frames.length < 1 || frames.length > MAX_FRAMES) return null;
  if (!frames.every((count) => isWholeIn(count, 0, 10))) return null;
  const filled = frames as number[];
  const given = record.added ?? filled.map(() => 0);
  if (!Array.isArray(given) || given.length !== filled.length) return null;
  if (!given.every((count, at) => isWholeIn(count, 0, filled[at] ?? 0))) {
    return null;
  }
  const added = given as number[];
  const first = filled.map((count, at) => count - (added[at] ?? 0));
  const firstTotal = first.reduce((sum, count) => sum + count, 0);
  const addedTotal = added.reduce((sum, count) => sum + count, 0);
  // Empty frames alone are not a question to count.
  if (firstTotal + addedTotal === 0) return null;
  // Adding on starts from some counters of the first kind.
  if (addedTotal > 0 && firstTotal === 0) return null;
  // Counters go in in order: once the second kind starts in a frame, no
  // later frame holds the first kind.
  for (let at = 0; at < filled.length - 1; at += 1) {
    if ((added[at] ?? 0) > 0 && first.slice(at + 1).some((n) => n > 0)) {
      return null;
    }
  }
  return { type: 'ten_frame', frames: [...filled], added: [...added] };
}

// What a screen reader is told in place of the picture: how many frames
// and whether there are two kinds of counter, never how many counters.
export function describeTenFrame(spec: TenFrameSpec): string {
  const frames =
    spec.frames.length === 1
      ? 'A ten frame with counters to count.'
      : 'Two ten frames with counters to count.';
  return spec.added.some((count) => count > 0)
    ? `${frames} Some counters are solid and some are rings.`
    : frames;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type TenFrameCounter = {
  kind: 'first' | 'added';
  cx: number;
  cy: number;
};

export type TenFramePicture = {
  frames: {
    x: number;
    y: number;
    // Every box, filled or not, in filling order.
    boxes: { x: number; y: number }[];
    counters: TenFrameCounter[];
  }[];
  box: number;
  // The radius of one counter.
  radius: number;
  width: number;
  height: number;
};

export const TEN_FRAME_BOX = 40;
const FRAME_GAP = 16;
const EDGE = 8;

// Two frames sit one above the other, so they fit a phone at a size small
// fingers and eyes can count.
export function tenFramePicture(spec: TenFrameSpec): TenFramePicture {
  const box = TEN_FRAME_BOX;
  const frameHeight = box * 2;
  const frames = spec.frames.map((filled, at) => {
    const x = EDGE;
    const y = EDGE + at * (frameHeight + FRAME_GAP);
    const boxes = Array.from({ length: 10 }, (_, index) => ({
      x: x + (index % 5) * box,
      y: y + Math.floor(index / 5) * box,
    }));
    const firstCount = filled - (spec.added[at] ?? 0);
    const counters = boxes.slice(0, filled).map((place, index) => ({
      kind: index < firstCount ? ('first' as const) : ('added' as const),
      cx: place.x + box / 2,
      cy: place.y + box / 2,
    }));
    return { x, y, boxes, counters };
  });
  return {
    frames,
    box,
    radius: box * 0.36,
    width: box * 5 + EDGE * 2,
    height:
      spec.frames.length * frameHeight +
      (spec.frames.length - 1) * FRAME_GAP +
      EDGE * 2,
  };
}
